// «Команда» (R-A2…R-A6, R-U27, decision 038 п.6): people and roles as tables over the whole width, as Linear's members — filter, search,
// «+»; the card of a person or a role opens in the stack of modals (R-U19), a role from a person and a person from a role lie on top.

import { useEffect, useMemo, useState } from 'react';
import { avatarUrl, grantLive, recordTitle, uuidv7, type RoleDef, type UserDef } from '@confdeck/core-shared';
import { useMeta, useSync, useTypesTick } from '../sync/react.tsx';
import { leaveThen } from '../shell/unsaved.ts';
import { ModalStack } from '../ui/ModalStack.tsx';
import { CompactTable } from '../table/CompactTable.tsx';
import { ago } from '../fields/format.ts';
import { Avatar, Button, Chip, Dialog, Field, Input, Menu, MenuContent, MenuItem, MenuTrigger, SearchField, Segmented, Tabs, useToast } from '../ui/primitives.tsx';
import { ExternalDialog } from './ExternalDialog.tsx';
import { PersonCard } from './PersonCard.tsx';
import { RoleCard } from './RoleCard.tsx';
import s from './team.module.css';

type Tab = 'people' | 'roles';
interface Layer { kind: 'person' | 'role'; id: string }
const keyOf = (l: Layer) => `${l.kind}:${l.id}`;

/** What a person's access is today, in one word and a colour: the column «Статус» and its filter. */
type PersonState = 'owner' | 'live' | 'pending' | 'external' | 'none' | 'off';
const STATE: Record<PersonState, { label: string; color: string }> = {
  owner: { label: 'Владелец', color: 'violet' }, live: { label: 'Действует', color: 'green' }, pending: { label: 'Приглашён', color: 'amber' },
  external: { label: 'Внештатный', color: 'blue' }, none: { label: 'Без ролей', color: 'gray' }, off: { label: 'Роли отключены', color: 'gray' },
};
function stateOf(u: UserDef): PersonState {
  if (u.role === 'owner') return 'owner';
  if (u.pending) return 'pending';
  const live = (u.grants ?? []).some((g) => grantLive(g));
  if (u.external) return live ? 'external' : 'none';
  return live ? 'live' : u.grants?.length ? 'off' : 'none';
}
const PEOPLE_FILTER = [{ value: 'all', label: 'Все' }, { value: 'live', label: 'Действуют' }, { value: 'pending', label: 'Приглашены' }, { value: 'external', label: 'Внештатные' }, { value: 'none', label: 'Без доступа' }] as const;
const ROLE_FILTER = [{ value: 'all', label: 'Все' }, { value: 'base', label: 'Базовые' }, { value: 'own', label: 'Свои' }] as const;
type PeopleFilter = (typeof PEOPLE_FILTER)[number]['value'];
const FILTER_PICKS: Record<PeopleFilter, (u: UserDef) => boolean> = {
  all: () => true,
  live: (u) => ['owner', 'live', 'external'].includes(stateOf(u)),
  pending: (u) => stateOf(u) === 'pending',
  external: (u) => !!u.external,
  none: (u) => ['none', 'off'].includes(stateOf(u)),
};
type RoleFilter = (typeof ROLE_FILTER)[number]['value'];

/** When each person last logged in: the owner's request to the server, once per visit of the screen; others are not asked (P3-5). */
function useLogins(owner: boolean): Record<string, string> {
  const [v, setV] = useState<Record<string, string>>({});
  useEffect(() => { if (owner) void fetch('/api/team/logins').then(async (r) => { if (r.ok) setV(((await r.json()) as { logins: Record<string, string> }).logins); }).catch(() => {}); }, [owner]);
  return v;
}

export function TeamScreen() {
  const meta = useMeta();
  const client = useSync();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('people');
  const [q, setQ] = useState('');
  const [peopleFilter, setPeopleFilter] = useState<PeopleFilter>('all');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [layers, setLayers] = useState<Layer[]>([]);
  const [inviting, setInviting] = useState(false);
  const [external, setExternal] = useState(false);
  const logins = useLogins(meta.user(client.header?.userId)?.role === 'owner');
  const [naming, setNaming] = useState(false);

  /** Opens a card on top; a card already in the stack comes back instead of lying twice. */
  const open = (l: Layer) => {
    const at = layers.findIndex((x) => keyOf(x) === keyOf(l));
    if (at < 0) { setLayers([...layers, l]); return; }
    closeFrom(at + 1);
  };
  /** Closes layer `i` and the ones above it, asking about their unsaved work only. */
  const closeFrom = (i: number) => {
    if (i >= layers.length) return;
    leaveThen(() => setLayers((cur) => cur.slice(0, i)), layers.slice(i).map(keyOf));
  };
  const closeAll = () => closeFrom(0);

  const people = meta.users().filter((u) => !u.archivedAt);
  const roles = meta.roles().filter((r) => !r.archivedAt);
  const roleNames = (u: UserDef) => (u.grants ?? []).filter((g) => grantLive(g)).map((g) => meta.role(g.roleId)?.name).filter(Boolean).join(', ');
  const scope = client.header?.scope;
  // The events come after the first render: the names follow their records, so «Доступ» never shows «—» for a known event (P3-1).
  const eventsTick = useTypesTick(scope ? [scope.typeId] : []);
  const eventNames = useMemo(() => {
    const map = new Map<string, string>();
    if (scope) for (const id of client.store.recordIds(scope.typeId)) { const r = client.store.record(id); if (r) map.set(id, recordTitle(r, meta)); }
    return map;
  }, [scope, client.store, meta, eventsTick]);
  const access = (u: UserDef) => {
    if (u.role === 'owner') return 'Всё';
    const live = (u.grants ?? []).filter((g) => grantLive(g));
    if (!live.length) return '—';
    if (live.some((g) => !g.scopeIds)) return 'Все';
    return [...new Set(live.flatMap((g) => g.scopeIds ?? []))].map((id) => eventNames.get(id) ?? '—').join(', ');
  };
  const peopleOf = (r: RoleDef) => people.filter((u) => u.grants?.some((g) => g.roleId === r.id)).length;

  const needle = q.trim().toLowerCase();
  const shownPeople = people.filter((u) => {
    if (!FILTER_PICKS[peopleFilter](u)) return false;
    return !needle || [u.name, u.email, roleNames(u)].some((x) => x.toLowerCase().includes(needle));
  });
  const shownRoles = roles.filter((r) => (roleFilter === 'all' || (roleFilter === 'base') === !!r.base) && (!needle || [r.name, r.description ?? ''].some((x) => x.toLowerCase().includes(needle))));

  // A role is made once it has a name (P3-3): an unnamed «Новая роль» does not stay behind a closed card.
  const createRole = async (name: string): Promise<string | null> => {
    const id = uuidv7();
    const r = await client.run('role.save', { id, name, perms: {} });
    if (!r.ok) return r.error.message;
    setNaming(false);
    open({ kind: 'role', id });
    return null;
  };
  const switchTab = (t: Tab) => { setTab(t); setQ(''); };
  const filtered = tab === 'people' ? peopleFilter !== 'all' : roleFilter !== 'all';

  return (
    <div className={s.screen}>
      <Tabs bar label="Команда" value={tab} onChange={switchTab} items={[{ key: 'people', label: 'Люди', count: people.length }, { key: 'roles', label: 'Роли', count: roles.length }]} />
      <div className={s.bar}>
        {tab === 'people'
          ? <Segmented size="sm" label="Показать людей" value={peopleFilter} onChange={setPeopleFilter} items={PEOPLE_FILTER.map((f) => ({ ...f }))} className={s.barFilter} />
          : <Segmented size="sm" label="Показать роли" value={roleFilter} onChange={setRoleFilter} items={ROLE_FILTER.map((f) => ({ ...f }))} className={s.barFilter} />}
        <SearchField size="sm" className={s.barSearch} value={q} onChange={setQ} label={tab === 'people' ? 'Поиск по людям' : 'Поиск по ролям'} placeholder={tab === 'people' ? 'Имя, email или роль' : 'Название роли'} />
        <span className={s.spacer} />
        <span className={s.help}>{tab === 'people' ? `${shownPeople.length} из ${people.length}` : `${shownRoles.length} из ${roles.length}`}</span>
        {tab === 'people' ? (
          <Menu>
            <MenuTrigger asChild><Button size="sm" variant="primary" icon="plus" caret>Человек</Button></MenuTrigger>
            <MenuContent>
              <MenuItem icon="mail" onSelect={() => setInviting(true)}>Пригласить в команду</MenuItem>
              <MenuItem icon="user" onSelect={() => setExternal(true)}>Добавить внештатного</MenuItem>
            </MenuContent>
          </Menu>
        ) : <Button size="sm" variant="primary" icon="plus" onClick={() => setNaming(true)}>Роль</Button>}
      </div>
      <div className={s.tableScroll}>
        {tab === 'people' ? (
          shownPeople.length ? (
            <CompactTable className={s.wide} cardsOnPhone label="Люди" rows={shownPeople} rowKey={(u) => u.id} onOpen={(u) => open({ kind: 'person', id: u.id })} columns={[
              { key: 'name', label: 'Имя', className: s.nameCol, render: (u) => <span className={s.person}><Avatar name={u.name} color={u.color} src={avatarUrl(u)} size={24} /><span className={s.cellName} title={u.name}>{u.name}</span></span> },
              { key: 'email', label: 'Email', render: (u) => <span className={s.cellText} title={u.email}>{u.email}</span> },
              { key: 'role', label: 'Роль', render: (u) => <span className={s.cellText} title={roleNames(u)}>{u.role === 'owner' ? 'Владелец аккаунта' : roleNames(u) || '—'}</span> },
              { key: 'access', label: scope ? `Доступ: ${(meta.type(scope.typeId)?.nameMany ?? scope.label).toLowerCase()}` : 'Доступ', render: (u) => <span className={s.cellText} title={access(u)}>{access(u)}</span> },
              { key: 'state', label: 'Статус', render: (u) => <Chip color={STATE[stateOf(u)].color}>{STATE[stateOf(u)].label}</Chip> },
              { key: 'login', label: 'Последний вход', render: (u) => (logins[u.id] ? <span title={new Date(logins[u.id]!).toLocaleString('ru')}>{ago(logins[u.id]!)}</span> : '—') },
            ]} />
          ) : <Empty filtered={filtered || !!needle} onReset={() => { setQ(''); setPeopleFilter('all'); }} text="Людей пока нет" />
        ) : (
          shownRoles.length ? (
            <CompactTable className={s.wide} cardsOnPhone label="Роли" rows={shownRoles} rowKey={(r) => r.id} onOpen={(r) => open({ kind: 'role', id: r.id })} columns={[
              { key: 'name', label: 'Роль', className: s.nameCol, render: (r) => <span className={s.cellName} title={r.name}>{r.name}</span> },
              { key: 'about', label: 'Описание', render: (r) => <span className={s.cellText} title={r.description ?? ''}>{r.description || (r.full ? 'Все разделы и действия' : '—')}</span> },
              { key: 'sections', label: 'Разделы', render: (r) => (r.full ? 'все' : String(Object.keys(r.perms).length)) },
              { key: 'people', label: 'Людей', render: (r) => String(peopleOf(r)) },
              { key: 'kind', label: 'Тип', render: (r) => <Chip color={r.base ? 'gray' : 'blue'}>{r.base ? 'базовая' : 'своя'}</Chip> },
            ]} />
          ) : <Empty filtered={filtered || !!needle} onReset={() => { setQ(''); setRoleFilter('all'); }} text="Ролей пока нет" />
        )}
      </div>
      <ModalStack onClose={closeFrom} layers={layers.map((l) => {
        const key = keyOf(l);
        if (l.kind === 'person') {
          const u = meta.user(l.id);
          return { key, label: `Человек «${u?.name ?? ''}»`, size: 'card' as const, content: u && !u.archivedAt
            ? <PersonCard key={key} user={u} layer={key} onOpenRole={(id) => open({ kind: 'role', id })} onClose={() => closeFrom(layers.indexOf(l))} />
            : <Gone onClose={() => closeFrom(layers.indexOf(l))} /> };
        }
        const r = meta.role(l.id);
        return { key, label: `Роль «${r?.name ?? ''}»`, size: 'card' as const, content: r && !r.archivedAt
          ? <RoleCard key={key} role={r} layer={key} onOpenPerson={(id) => open({ kind: 'person', id })} onGrant={() => { closeAll(); switchTab('people'); }} onClose={() => closeFrom(layers.indexOf(l))} />
          : <Gone onClose={() => closeFrom(layers.indexOf(l))} /> };
      })} />
      {external && <ExternalDialog onClose={() => setExternal(false)} onDone={(id) => { setExternal(false); open({ kind: 'person', id }); toast({ kind: 'info', text: 'Внештатный добавлен: выдайте ему роль' }); }} />}
      {naming && <NewRole onClose={() => setNaming(false)} onCreate={createRole} />}
      {inviting && <Invite onClose={() => setInviting(false)} onDone={(id) => { setInviting(false); open({ kind: 'person', id }); toast({ kind: 'info', text: 'Приглашение отправлено: человек задаст пароль по ссылке из письма' }); }} />}
    </div>
  );
}

function Empty({ filtered, onReset, text }: { filtered: boolean; onReset: () => void; text: string }) {
  return <div className={s.empty}>{filtered ? <>Никого не нашли. <Button size="sm" variant="ghost" onClick={onReset}>Сбросить</Button></> : text}</div>;
}

/** The person or the role of an open card was removed (here or by someone else): the card says so instead of going blank. */
function Gone({ onClose }: { onClose: () => void }) {
  return <div className={s.empty}>Уже удалено. <Button size="sm" variant="ghost" onClick={onClose}>Закрыть</Button></div>;
}

function NewRole({ onClose, onCreate }: { onClose: () => void; onCreate: (name: string) => Promise<string | null> }) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => { e.preventDefault(); if (name.trim()) setError(await onCreate(name.trim())); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} label="Новая роль">
      <form onSubmit={submit} className={s.dialog}>
        <h2 className={s.dialogTitle}>Новая роль</h2>
        <Field label="Название"><Input autoFocus placeholder="Например, «Модератор чата»" value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <p className={s.help}>Права по разделам и действия настроите в карточке роли, людям её выдают в их карточках.</p>
        {error && <p className={s.error} role="alert">{error}</p>}
        <div className={s.actions}><span className={s.spacer} /><Button onClick={onClose}>Отмена</Button><Button type="submit" variant="primary" disabled={!name.trim()}>Создать</Button></div>
      </form>
    </Dialog>
  );
}

function Invite({ onClose, onDone }: { onClose: () => void; onDone: (id: string) => void }) {
  const client = useSync();
  const [v, setV] = useState({ name: '', email: '' });
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const id = uuidv7();
    const r = await client.run('user.invite', { id, ...v });
    if (r.ok) onDone(id); else setError(r.error.message);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} label="Пригласить в команду">
      <form onSubmit={submit} className={s.dialog}>
        <h2 className={s.dialogTitle}>Пригласить в команду</h2>
        {(['name', 'email'] as const).map((k) => (
          <Field key={k} label={<>{k === 'name' ? 'Имя и фамилия' : 'Email — логин'}</>}>
            <Input type={k === 'email' ? 'email' : 'text'} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} required /></Field>
        ))}
        <p className={s.help}>На email уйдёт письмо со ссылкой: по ней человек сам задаст пароль. Ссылка действует 7 дней и работает один раз.</p>
        <p className={s.help}>Без ролей у человека нет доступа: после приглашения выдайте роль — «Администратор» даёт всё, другие роли — свои разделы и события.</p>
        {error && <p className={s.error} role="alert">{error}</p>}
        <div className={s.actions}><Button onClick={onClose}>Отмена</Button><Button type="submit" variant="primary">Пригласить</Button></div>
      </form>
    </Dialog>
  );
}
